import React, { useMemo, useState } from "react";
import { Pill, Check, X, Loader2, AlertCircle, Clock, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { BottomSheet } from "@/components/BottomSheet";
import { localYmd } from "@/lib/format";

/**
 * MedicationDosesCard — renders medication doses scheduled for today PLUS
 * any un-logged doses from the last 7 days ("missed"). Missed doses surface
 * a time picker so users can backdate adherence accurately when they actually
 * took the dose at a different time (e.g. took yesterday's evening dose
 * 30min after midnight, or forgot to mark it until the next morning).
 *
 * UX:
 *   - Today's doses        → tap Take / Skip → instant log
 *   - Past doses (missed)  → tap Take → opens BackdateSheet → confirm time → log
 *                            tap Skip → instant log (skip time is irrelevant)
 *
 * Backend attributes the dose to the correct calendar day via:
 *   - explicit `done_at_override` from BackdateSheet, OR
 *   - dose.start_at when the scheduled time is >30min past (server default)
 */
export function MedicationDosesCard({ doses, familyId, onLogged, testid = "meds-card" }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState({}); // {doseId: "taken"|"skipped"}
  const [hidden, setHidden] = useState(new Set());
  const [checkInPrompt, setCheckInPrompt] = useState(null);
  const [backdating, setBackdating] = useState(null); // dose object when picker is open

  // Tap on a dose row → jump straight to the medication info / setup screen
  // (Health section of ProfileSetup). The dose carries `medication_id`
  // plus `assignee_person_ids` (the owner of this med) so we can deep-link
  // into that person's profile via ?openMed=&person=.
  const openMedicationInfo = (dose) => {
    if (!dose?.medication_id) return;
    const personId =
      dose.person_id ||
      (dose.assignee_person_ids && dose.assignee_person_ids[0]) ||
      (dose.participant_person_ids && dose.participant_person_ids[0]) ||
      "";
    navigate(`/you/profile?openMed=${dose.medication_id}&person=${personId}`);
  };

  const log = async (dose, status, doneAtOverride = null) => {
    if (pending[dose.id]) return;
    setPending((p) => ({ ...p, [dose.id]: status }));
    try {
      const { data } = await api.post(`/families/${familyId}/dose-log`, {
        dose_item_id: dose.id,
        status,
        ...(doneAtOverride ? { done_at_override: doneAtOverride } : {}),
      });
      setHidden((s) => new Set(s).add(dose.id));
      if (data?.needs_check_in) setCheckInPrompt({ med: dose.title });
      onLogged?.();
    } catch (_e) {
      setPending((p) => { const n = { ...p }; delete n[dose.id]; return n; });
    } finally {
      setBackdating(null);
    }
  };

  const onTake = (dose, isMissed) => {
    if (isMissed) {
      setBackdating(dose);   // open picker
    } else {
      log(dose, "taken");
    }
  };

  // Bucket into today vs missed. Today = same wall-clock date as user's "now".
  const { todayDoses, missedDoses } = useMemo(() => {
    const today = localYmd(new Date());
    const t = [], m = [];
    for (const d of (doses || [])) {
      if (hidden.has(d.id)) continue;
      const ymd = d.start_at ? localYmd(new Date(d.start_at)) : today;
      (ymd === today ? t : m).push(d);
    }
    // Missed sorted MOST-RECENT first so yesterday's dose is at the top.
    m.sort((a, b) => (b.start_at || "").localeCompare(a.start_at || ""));
    t.sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
    return { todayDoses: t, missedDoses: m };
  }, [doses, hidden]);

  const totalVisible = todayDoses.length + missedDoses.length;
  if (totalVisible === 0 && !checkInPrompt) return null;

  return (
    <section className="kinly-card" data-testid={testid}>
      <header className="flex items-center gap-2 mb-3">
        <Pill className="w-4 h-4 text-primary" />
        <p className="font-display text-[15px] font-medium leading-tight">Medication</p>
        <span className="ml-auto text-[11px] uppercase tracking-wider text-muted-foreground tabular-nums">
          {totalVisible}
        </span>
      </header>

      {missedDoses.length > 0 && (
        <div className="mb-3" data-testid="meds-missed-section">
          <p className="text-[10px] uppercase tracking-[0.18em] text-destructive font-medium mb-1.5 inline-flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Missed
          </p>
          <ul className="space-y-2">
            {missedDoses.map((d) => (
              <DoseRow
                key={d.id}
                dose={d}
                pending={pending[d.id]}
                missed
                onTake={() => onTake(d, true)}
                onSkip={() => log(d, "skipped")}
                onInfo={() => openMedicationInfo(d)}
              />
            ))}
          </ul>
        </div>
      )}

      {todayDoses.length > 0 && (
        <ul className="space-y-2" data-testid="meds-card-list">
          {todayDoses.map((d) => (
            <DoseRow
              key={d.id}
              dose={d}
              pending={pending[d.id]}
              onTake={() => onTake(d, false)}
              onSkip={() => log(d, "skipped")}
              onInfo={() => openMedicationInfo(d)}
            />
          ))}
        </ul>
      )}

      {checkInPrompt && (
        <div
          data-testid="meds-checkin-banner"
          className="mt-3 rounded-xl border border-primary/30 bg-primary/[0.06] p-3"
        >
          <p className="text-[13px] font-medium leading-snug">
            Kinly noticed a few missed doses of {checkInPrompt.med}.
          </p>
          <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
            All good? If side effects or timing are off, a quick chat with your
            doctor or pharmacist often helps. Kinly's here either way.
          </p>
        </div>
      )}

      <BackdateSheet
        open={!!backdating}
        dose={backdating}
        onCancel={() => setBackdating(null)}
        onConfirm={(iso) => log(backdating, "taken", iso)}
      />
    </section>
  );
}

function DoseRow({ dose: d, pending, missed, onTake, onSkip, onInfo }) {
  const isPending = !!pending;
  return (
    <li
      data-testid={`dose-row-${d.id}`}
      className={[
        "flex items-center gap-3 py-2 border-t border-border/40 first:border-t-0 first:pt-0",
        missed ? "opacity-90" : "",
      ].join(" ")}
    >
      <button
        type="button"
        data-testid={`dose-info-${d.id}`}
        onClick={onInfo}
        className="flex items-center gap-3 flex-1 min-w-0 text-left tap"
        aria-label={`Open ${d.title} info`}
      >
        <span className="font-mono text-[10px] tabular-nums text-muted-foreground w-12 leading-tight shrink-0">
          {missed && (
            <span className="block text-destructive font-medium">{formatDate(d.start_at)}</span>
          )}
          <span className="block">{formatTime(d.start_at)}</span>
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-medium leading-tight truncate">
            {d.title}
            {d.medication_dose && (
              <span className="text-muted-foreground font-normal"> · {d.medication_dose}</span>
            )}
          </p>
          {(d.medication_with_food || d.medication_instructions) && (
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
              {d.medication_with_food ? "With food" : null}
              {d.medication_with_food && d.medication_instructions ? " · " : null}
              {d.medication_instructions || ""}
            </p>
          )}
        </div>
        <Info className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
      </button>
      <button
        type="button"
        data-testid={`dose-skip-${d.id}`}
        onClick={onSkip}
        disabled={isPending}
        aria-label="Skip"
        className="w-9 h-9 rounded-full border border-border bg-card tap inline-flex items-center justify-center text-muted-foreground disabled:opacity-40"
      >
        {pending === "skipped" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-4 h-4" />}
      </button>
      <button
        type="button"
        data-testid={`dose-take-${d.id}`}
        onClick={onTake}
        disabled={isPending}
        aria-label={missed ? "Take (pick time)" : "Take"}
        className="w-9 h-9 rounded-full bg-primary text-primary-foreground tap inline-flex items-center justify-center disabled:opacity-40"
      >
        {pending === "taken"
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : missed
            ? <Clock className="w-4 h-4" />
            : <Check className="w-4 h-4" />}
      </button>
    </li>
  );
}

function BackdateSheet({ open, dose, onCancel, onConfirm }) {
  // Seed picker with the dose's scheduled time — most accurate default
  // for "I took it on schedule, just forgot to log it".
  const seed = dose?.start_at ? new Date(dose.start_at) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const [date, setDate] = useState(localYmd(seed));
  const [time, setTime] = useState(`${pad(seed.getHours())}:${pad(seed.getMinutes())}`);

  // Reset when a different dose is selected
  React.useEffect(() => {
    if (!dose) return;
    const s = dose.start_at ? new Date(dose.start_at) : new Date();
    setDate(localYmd(s));
    setTime(`${pad(s.getHours())}:${pad(s.getMinutes())}`);
  }, [dose]);

  if (!open || !dose) return null;

  const submit = () => {
    const iso = new Date(`${date}T${time}:00`).toISOString();
    onConfirm(iso);
  };

  return (
    <BottomSheet open onClose={onCancel} title="When did you take it?">
      <div data-testid="meds-backdate-sheet" className="pb-3 space-y-4">
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          Setting the exact time keeps your adherence streak honest.
          Scheduled for {formatDate(dose.start_at)} at {formatTime(dose.start_at)}.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">Date</span>
            <input
              type="date"
              data-testid="meds-backdate-date"
              value={date}
              max={localYmd(new Date())}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-card border border-border rounded-xl px-3 py-2 mt-1 outline-none text-[15px]"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">Time</span>
            <input
              type="time"
              data-testid="meds-backdate-time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full bg-card border border-border rounded-xl px-3 py-2 mt-1 outline-none text-[15px]"
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-11 rounded-2xl border border-border text-sm font-medium tap"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="meds-backdate-confirm"
            onClick={submit}
            className="h-11 rounded-2xl bg-foreground text-background text-sm font-medium tap inline-flex items-center justify-center gap-1.5"
          >
            <Check className="w-4 h-4" /> Mark as taken
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const ymd = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    if (ymd(d) === ymd(today)) return "Today";
    const yesterday = new Date(today.getTime() - 86400000);
    if (ymd(d) === ymd(yesterday)) return "Yesterday";
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  } catch {
    return "";
  }
}
